import { DragEvent, FC, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import { useQueryClient } from 'react-query'
import { KTIcon } from '../../../_metronic/helpers'
import { axiosClientAuth } from '../../utils/axiosClient'
import { KybStatusData } from '../../hooks/useKybStatus'

// ── DROPDOWN OPTIONS ──────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'BANKING_INSURANCE_FINANCIAL', label: 'Banking/Insurance/Financial products/credit cards' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'CONSUMER_GOODS_AUTOMOBILES', label: 'Consumer goods and automobiles' },
  { value: 'COMMUNICATION_BROADCASTING_IT', label: 'Communication/Broadcasting/Entertainment/IT' },
  { value: 'TOURISM_LEISURE', label: 'Tourism and Leisure' },
  { value: 'FOOD_BEVERAGES', label: 'Food and Beverages' },
]

const COUNTRIES = [
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SG', label: 'Singapore' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'NP', label: 'Nepal' },
  { value: 'LA', label: 'Laos' },
  { value: 'OT', label: 'Other' },
]

const BUSINESS_TYPES = [
  { value: 'PROPRIETORSHIP', label: 'Proprietorship' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'LLP', label: 'Limited Liability Partnership' },
  { value: 'PRIVATE_LIMITED', label: 'Private Limited Company' },
  { value: 'PUBLIC_LIMITED', label: 'Public Limited Company' },
  { value: 'GOVERNMENT_PSU', label: 'Government/PSU' },
  { value: 'TRUST_SOCIETY_NGO', label: 'Trust/Society/NGO' },
  { value: 'OTHERS', label: 'Others' },
]

const KYB_DOCS = [
  { key: 'document_ENT_REGN_CERT', label: 'Enterprise Registration Certificate', hint: 'PDF, JPG, PNG up to 5 MB' },
  { key: 'document_TAX_INDT_NO', label: 'Taxpayer Identification Number (TIN)', hint: 'PDF, JPG, PNG up to 5 MB' },
  { key: 'document_VAT_REGN', label: 'VAT Registration / Certificate', hint: 'PDF, JPG, PNG up to 5 MB' },
]

// ── DOCUMENT UPLOAD ───────────────────────────────────────────────────────────

interface DocSlot { file: File | null; existingName?: string }

const KybDocumentUpload: FC<{
  docs: DocSlot[]
  setDocs: React.Dispatch<React.SetStateAction<DocSlot[]>>
  activeDoc: number
  setActiveDoc: React.Dispatch<React.SetStateAction<number>>
}> = ({ docs, setDocs, activeDoc, setActiveDoc }) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const setFile = (index: number, file: File | null) =>
    setDocs((prev) => prev.map((d, i) => (i === index ? { ...d, file } : d)))

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (file) setFile(activeDoc, file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0] ?? null
    if (file) setFile(activeDoc, file)
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const currentDoc = docs[activeDoc]

  return (
    <div className='row g-4 align-items-stretch' style={{ minHeight: 220 }}>
      <div className='col-6 d-flex flex-column gap-3'>
        {KYB_DOCS.map((doc, i) => (
          <button
            key={i}
            type='button'
            onClick={() => setActiveDoc(i)}
            className={clsx(
              'btn text-start d-flex align-items-center gap-3 py-3 px-4 rounded-2',
              activeDoc === i ? 'btn-primary' : 'btn-light'
            )}
          >
            <KTIcon iconName='document' className={clsx('fs-2', activeDoc === i ? 'text-white' : 'text-primary')} />
            <div className='overflow-hidden'>
              <div className='fw-semibold fs-7 text-truncate'>{doc.label}</div>
              <div
                className={clsx(
                  'fs-8',
                  (docs[i].file || docs[i].existingName)
                    ? activeDoc === i ? 'text-white opacity-75' : 'text-success'
                    : activeDoc === i ? 'text-white opacity-50' : 'text-muted'
                )}
              >
                {docs[i].file || docs[i].existingName ? 'Uploaded' : 'Not uploaded'}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div
        className={clsx(
          'col-6 rounded-2 d-flex flex-column align-items-center justify-content-center p-6',
          isDragging ? 'border border-primary border-2 bg-light-primary' : 'border border-dashed bg-light'
        )}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
      >
        {currentDoc.file ? (
          <div className='d-flex flex-column align-items-center gap-3 text-center'>
            <div
              className='d-flex align-items-center justify-content-center rounded-circle bg-light-success'
              style={{ width: 56, height: 56 }}
            >
              <KTIcon iconName='check' className='fs-2 text-success' />
            </div>
            <div>
              <div className='fw-bold text-dark fs-6 mb-1'>{currentDoc.file.name}</div>
              <div className='text-muted fs-7'>{formatBytes(currentDoc.file.size)}</div>
            </div>
            <div className='d-flex gap-2'>
              <label className='btn btn-sm btn-light-primary'>
                Replace
                <input ref={fileInputRef} type='file' hidden accept='.pdf,.jpg,.jpeg,.png' onChange={handleFileChange} />
              </label>
              <button type='button' className='btn btn-sm btn-light-danger' onClick={() => setFile(activeDoc, null)}>
                <KTIcon iconName='trash' className='fs-4' />
                Delete
              </button>
            </div>
          </div>
        ) : currentDoc.existingName ? (
          <div className='d-flex flex-column align-items-center gap-3 text-center'>
            <div
              className='d-flex align-items-center justify-content-center rounded-circle bg-light-success'
              style={{ width: 56, height: 56 }}
            >
              <KTIcon iconName='check' className='fs-2 text-success' />
            </div>
            <div>
              <div className='fw-bold text-dark fs-6 mb-1'>{currentDoc.existingName}</div>
              <div className='text-muted fs-7'>Previously uploaded</div>
            </div>
            <label className='btn btn-sm btn-light-primary'>
              Replace
              <input ref={fileInputRef} type='file' hidden accept='.pdf,.jpg,.jpeg,.png' onChange={handleFileChange} />
            </label>
          </div>
        ) : (
          <div className='d-flex flex-column align-items-center text-center'>
            <KTIcon iconName='file-up' className='fs-3x text-primary mb-3' />
            <p className='fw-semibold text-dark mb-1 fs-6'>Drag & drop file here</p>
            <p className='text-muted fs-7 mb-1'>{KYB_DOCS[activeDoc].hint}</p>
            <p className='text-muted fs-8 mb-4'>or</p>
            <label className='btn btn-sm btn-primary'>
              Browse Files
              <input ref={fileInputRef} type='file' hidden accept='.pdf,.jpg,.jpeg,.png' onChange={handleFileChange} />
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

// ── FORM FIELD HELPERS ────────────────────────────────────────────────────────

const FieldError: FC<{ msg?: string }> = ({ msg }) =>
  msg ? <div className='fv-plugins-message-container'><span className='fv-help-block'>{msg}</span></div> : null

// ── VALIDATION SCHEMA ─────────────────────────────────────────────────────────

const resubmitSchema = Yup.object().shape({
  businessType: Yup.string().required('Business type is required'),
  category: Yup.string().required('Category is required'),
  legalEntityName: Yup.string().required('Legal entity name is required').max(200, 'Maximum 200 characters'),
  brandName: Yup.string().max(200, 'Maximum 200 characters'),
  registrationNumber: Yup.string().required('Registration number is required'),
  addressLine1: Yup.string().required('Address is required'),
  addressLine2: Yup.string(),
  country: Yup.string().required('Country is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string(),
  postalCode: Yup.string().required('Postal / ZIP code is required'),
  authorisedPersonName: Yup.string().required('Authorised person name is required'),
  department: Yup.string().required('Department is required').max(100, 'Maximum 100 characters'),
  contactNumber: Yup.string().required('Contact number is required'),
  email: Yup.string().email(),
})

// ── INLINE FORM ───────────────────────────────────────────────────────────────

interface Props {
  onHide: () => void
  prefill: KybStatusData | null | undefined
}

const KybResubmitInline: FC<Props> = ({ onHide, prefill }) => {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [docs, setDocs] = useState<DocSlot[]>([{ file: null }, { file: null }, { file: null }])
  const [activeDoc, setActiveDoc] = useState(0)

  useEffect(() => {
    setDocs(
      KYB_DOCS.map((kybDoc) => {
        const existing = prefill?.documents?.find(
          (d) => d.document_type === kybDoc.key.replace('document_', '')
        )
        return { file: null, existingName: existing?.file_name }
      })
    )
    setActiveDoc(0)
  }, [prefill])

  const formik = useFormik({
    initialValues: {
      businessType: prefill?.business_type ?? '',
      category: prefill?.category ?? '',
      legalEntityName: prefill?.business_name ?? '',
      brandName: prefill?.brand_name ?? '',
      registrationNumber: prefill?.registration_number ?? '',
      addressLine1: prefill?.address_line1 ?? '',
      addressLine2: prefill?.address_line2 ?? '',
      country: prefill?.country ?? '',
      city: prefill?.city ?? '',
      state: prefill?.state ?? '',
      postalCode: prefill?.zip_code ?? '',
      authorisedPersonName: prefill?.authorised_person_name ?? '',
      department: prefill?.department ?? '',
      contactNumber: prefill?.contact_phone ?? '',
      email: prefill?.email ?? '',
    },
    validationSchema: resubmitSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const formData = new FormData()
        formData.append('email', values.email)
        formData.append('registration_number', values.registrationNumber)
        formData.append('business_name', values.legalEntityName)
        if (values.brandName) formData.append('brand_name', values.brandName)
        formData.append('resource_type', 'TM')
        formData.append('business_type', values.businessType)
        formData.append('category', values.category)
        formData.append('country', values.country)
        formData.append('address_line1', values.addressLine1)
        if (values.addressLine2) formData.append('address_line2', values.addressLine2)
        formData.append('city', values.city)
        if (values.state) formData.append('state', values.state)
        formData.append('zip_code', values.postalCode)
        formData.append('authorised_person_name', values.authorisedPersonName)
        formData.append('department', values.department)
        formData.append('contact_phone', values.contactNumber)
        KYB_DOCS.forEach((doc, i) => {
          if (docs[i].file) formData.append(doc.key, docs[i].file as File)
        })

        await axiosClientAuth.post('/kyb/resubmit/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        await Swal.fire({
          icon: 'success',
          title: 'Resubmission successful',
          text: 'Your KYB details have been resubmitted and are under review.',
          confirmButtonText: 'OK',
        })

        await queryClient.invalidateQueries('kyb-status')
        onHide()
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? err?.response?.data?.detail ?? 'Something went wrong. Please try again.'
        Swal.fire({ icon: 'error', title: 'Submission failed', text: msg })
      } finally {
        setLoading(false)
      }
    },
  })

  const inputClass = (field: string) =>
    clsx('form-control form-control-lg form-control-solid', {
      'is-invalid': formik.touched[field as keyof typeof formik.touched] && formik.errors[field as keyof typeof formik.errors],
    })

  const selectClass = (field: string) =>
    clsx('form-select form-select-lg form-select-solid', {
      'is-invalid': formik.touched[field as keyof typeof formik.touched] && formik.errors[field as keyof typeof formik.errors],
    })

  return (
    <div className='card mt-4'>
      <div className='card-header'>
        <div className='card-title'>
          <h4 className='fw-bold text-dark mb-0'>Resubmit KYB Details</h4>
        </div>
        <div className='card-toolbar'>
          <div className='text-muted fs-7'>Update your information and upload required documents</div>
        </div>
      </div>

      <div className='card-body'>
        <form onSubmit={formik.handleSubmit} noValidate>

          {/* ── Business Details ── */}
          <h5 className='fw-bold text-dark fs-6 mb-5'>Business Details</h5>

          <div className='d-flex flex-column gap-5 mb-8'>
            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Business Type</label>
                <select className={selectClass('businessType')} {...formik.getFieldProps('businessType')}>
                  <option value=''>Select business type</option>
                  {BUSINESS_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <FieldError msg={formik.touched.businessType && formik.errors.businessType ? formik.errors.businessType : undefined} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Category</label>
                <select className={selectClass('category')} {...formik.getFieldProps('category')}>
                  <option value=''>Select category</option>
                  {CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <FieldError msg={formik.touched.category && formik.errors.category ? formik.errors.category : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Legal Entity Name</label>
                <input type='text' className={inputClass('legalEntityName')} placeholder='Enter legal entity name' {...formik.getFieldProps('legalEntityName')} />
                <FieldError msg={formik.touched.legalEntityName && formik.errors.legalEntityName ? formik.errors.legalEntityName : undefined} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label'>Brand Name <span className='text-muted'>(optional)</span></label>
                <input type='text' className={inputClass('brandName')} placeholder='Enter brand name' {...formik.getFieldProps('brandName')} />
                <FieldError msg={formik.touched.brandName && formik.errors.brandName ? formik.errors.brandName : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Registration Number</label>
                <input type='text' className={inputClass('registrationNumber')} placeholder='Enter registration number' {...formik.getFieldProps('registrationNumber')} />
                <FieldError msg={formik.touched.registrationNumber && formik.errors.registrationNumber ? formik.errors.registrationNumber : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12'>
                <label className='form-label required'>Address Line 1</label>
                <input type='text' className={inputClass('addressLine1')} placeholder='Street, building, area' {...formik.getFieldProps('addressLine1')} />
                <FieldError msg={formik.touched.addressLine1 && formik.errors.addressLine1 ? formik.errors.addressLine1 : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12'>
                <label className='form-label'>Address Line 2 <span className='text-muted'>(optional)</span></label>
                <input type='text' className={inputClass('addressLine2')} placeholder='Apartment, suite, floor' {...formik.getFieldProps('addressLine2')} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Country</label>
                <select className={selectClass('country')} {...formik.getFieldProps('country')}>
                  <option value=''>Select country</option>
                  {COUNTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <FieldError msg={formik.touched.country && formik.errors.country ? formik.errors.country : undefined} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>City</label>
                <input type='text' className={inputClass('city')} placeholder='Enter city' {...formik.getFieldProps('city')} />
                <FieldError msg={formik.touched.city && formik.errors.city ? formik.errors.city : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label'>State <span className='text-muted'>(optional)</span></label>
                <input type='text' className={inputClass('state')} placeholder='Enter state' {...formik.getFieldProps('state')} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Postal / ZIP Code</label>
                <input type='text' className={inputClass('postalCode')} placeholder='Enter postal code' {...formik.getFieldProps('postalCode')} />
                <FieldError msg={formik.touched.postalCode && formik.errors.postalCode ? formik.errors.postalCode : undefined} />
              </div>
            </div>
          </div>

          <div className='separator mb-8' />

          {/* ── Contact Details ── */}
          <h5 className='fw-bold text-dark fs-6 mb-5'>Contact Details</h5>

          <div className='d-flex flex-column gap-5 mb-8'>
            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Authorised Person Name</label>
                <input type='text' className={inputClass('authorisedPersonName')} placeholder='Full name' {...formik.getFieldProps('authorisedPersonName')} />
                <FieldError msg={formik.touched.authorisedPersonName && formik.errors.authorisedPersonName ? formik.errors.authorisedPersonName : undefined} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Department</label>
                <input type='text' className={inputClass('department')} placeholder='Enter department' {...formik.getFieldProps('department')} />
                <FieldError msg={formik.touched.department && formik.errors.department ? formik.errors.department : undefined} />
              </div>
            </div>

            <div className='row g-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label required'>Contact Number</label>
                <input type='text' className={inputClass('contactNumber')} placeholder='+856 20 xxxx xxxx' {...formik.getFieldProps('contactNumber')} />
                <FieldError msg={formik.touched.contactNumber && formik.errors.contactNumber ? formik.errors.contactNumber : undefined} />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label'>Email <span className='text-muted'>(optional)</span></label>
                <input type='email' className={inputClass('email')} placeholder='Enter email address' {...formik.getFieldProps('email')} />
                <FieldError msg={formik.touched.email && formik.errors.email ? formik.errors.email : undefined} />
              </div>
            </div>
          </div>

          <div className='separator mb-8' />

          {/* ── KYB Documents ── */}
          <h5 className='fw-bold text-dark fs-6 mb-5'>KYB Documents</h5>

          <p className='text-muted fs-7 mb-5'>
            Upload any documents you wish to replace. Previously uploaded documents will be retained if not replaced. Accepted formats: PDF, JPG, PNG (max 5 MB each).
          </p>

          <div className='mb-8'>
            <KybDocumentUpload docs={docs} setDocs={setDocs} activeDoc={activeDoc} setActiveDoc={setActiveDoc} />
          </div>

          {/* ── Footer ── */}
          <div className='d-flex justify-content-end gap-3 pt-4 border-top'>
            <button type='button' className='btn btn-light' onClick={onHide} disabled={loading}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary d-flex align-items-center gap-2'
              disabled={loading}
            >
              {loading && <span className='spinner-border spinner-border-sm' />}
              Submit
            </button>
          </div>

        </form>
      </div>
    </div>
  )
}

export default KybResubmitInline
